import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

interface BooleanFilterProps {
  /** Current value: true, false, or undefined (any) */
  value: boolean | undefined;

  /** Callback when value changes */
  onChange: (value: boolean | undefined) => void;

  /** Labels for the options */
  labels?: {
    true?: string;
    false?: string;
    any?: string;
  };

  /** Whether to show the "Any" option */
  showAnyOption?: boolean;
}

/**
 * Boolean filter with Yes/No/Any options
 *
 * Features:
 * - Radio button selection
 * - Customizable labels
 * - Optional "Any" to clear filter
 */
const BooleanFilter: React.FC<BooleanFilterProps> = ({
  value,
  onChange,
  labels = {},
  showAnyOption = true,
}) => {
  const theme = useTheme();

  const {
    true: trueLabel = "Yes",
    false: falseLabel = "No",
    any: anyLabel = "Any",
  } = labels;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue === "any") {
      onChange(undefined);
    } else {
      onChange(newValue === "true");
    }
  };

  const currentValue = value === undefined ? "any" : value ? "true" : "false";

  return (
    <RadioGroup value={currentValue} onChange={handleChange}>
      {showAnyOption && (
        <FormControlLabel
          value="any"
          control={
            <Radio
              size="small"
              sx={{
                p: 0.5,
                "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8125rem",
                color:
                  currentValue === "any"
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                fontWeight: currentValue === "any" ? 500 : 400,
              }}
            >
              {anyLabel}
            </Typography>
          }
          sx={{
            mx: 0,
            py: 0.25,
            px: 0.5,
            borderRadius: 1,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.5),
            },
            ...(currentValue === "any" && {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            }),
          }}
        />
      )}
      <FormControlLabel
        value="true"
        control={
          <Radio
            size="small"
            sx={{
              p: 0.5,
              "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8125rem",
              color:
                currentValue === "true"
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              fontWeight: currentValue === "true" ? 500 : 400,
            }}
          >
            {trueLabel}
          </Typography>
        }
        sx={{
          mx: 0,
          py: 0.25,
          px: 0.5,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: alpha(theme.palette.action.hover, 0.5),
          },
          ...(currentValue === "true" && {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }),
        }}
      />
      <FormControlLabel
        value="false"
        control={
          <Radio
            size="small"
            sx={{
              p: 0.5,
              "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8125rem",
              color:
                currentValue === "false"
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              fontWeight: currentValue === "false" ? 500 : 400,
            }}
          >
            {falseLabel}
          </Typography>
        }
        sx={{
          mx: 0,
          py: 0.25,
          px: 0.5,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: alpha(theme.palette.action.hover, 0.5),
          },
          ...(currentValue === "false" && {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }),
        }}
      />
    </RadioGroup>
  );
};

export default BooleanFilter;
